import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import TrustBadges from "@/components/TrustBadges";
import { Trophy, Heart, CheckCircle2, Star } from "lucide-react";

const monthKey = (offset) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const medal = ["bg-[#D99B26]", "bg-slate-400", "bg-[#B87333]", "bg-[#2E7D6B]", "bg-[#2E7D6B]"];

export default function HeroesBoard() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/heroes", { params: { month: monthKey(offset) } }).then((r) => setData(r.data)); }, [offset]);

  return (
    <section data-testid="heroes-board" className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-2 bg-[#FFF7E6] text-[#A9741A] px-4 py-1.5 rounded-full text-sm font-semibold mb-3"><Trophy className="w-4 h-4" /> Neighbourhood Heroes</span>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">The most thanked helpers {offset === 0 ? "this month" : "last month"}</h2>
          <p className="text-lg text-slate-600 mt-2">Celebrating the kind neighbours who showed up, again and again.</p>
        </div>
        <div className="flex gap-2">
          {[["This month", 0], ["Last month", 1]].map(([l, o]) => (
            <button key={o} data-testid={`heroes-toggle-${o}`} onClick={() => setOffset(o)}
              className={`px-4 min-h-[48px] rounded-xl font-semibold transition-colors ${offset === o ? "bg-[#2E7D6B] text-white" : "bg-white border-2 border-[#E2D9CE] text-slate-700 hover:border-[#2E7D6B]"}`}>{l}</button>
          ))}
        </div>
      </div>

      {!data ? null : data.heroes.length === 0 ? (
        <div data-testid="heroes-empty" className="mt-8 bg-white border border-[#E2D9CE] rounded-2xl p-10 text-center">
          <Heart className="w-10 h-10 text-[#D96B43] mx-auto" />
          <p className="text-xl font-semibold text-slate-800 mt-3">No heroes crowned for {data.label} yet.</p>
          <p className="text-slate-600 mt-1">Complete a job or send a thank-you card to put a helper on the board.</p>
        </div>
      ) : (
        <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {data.heroes.map((h, i) => (
            <li key={h.id}>
              <Link to={`/helpers/${h.id}`} data-testid={`hero-card-${h.id}`}
                className={`block bg-white border-2 rounded-2xl p-5 text-center hover:shadow-md transition-all ${i === 0 ? "border-[#D99B26] shadow-md" : "border-[#E2D9CE] hover:border-[#2E7D6B]"}`}>
                <div className="relative inline-block">
                  <Avatar user={h} size="lg" />
                  <span className={`absolute -top-1 -right-2 w-8 h-8 rounded-full ${medal[i]} text-white font-bold text-sm flex items-center justify-center shadow`}>#{i + 1}</span>
                </div>
                <p className="text-lg font-semibold text-slate-900 mt-3">{h.name}</p>
                <div className="flex flex-wrap justify-center gap-1 mt-1 min-h-[24px]"><TrustBadges user={h} /></div>
                <div className="flex justify-center gap-4 mt-3 text-slate-700 text-sm">
                  <span className="flex items-center gap-1" title="Thank-you notes"><Heart className="w-4 h-4 text-[#D96B43]" fill="#D96B43" /> {h.thanks}</span>
                  <span className="flex items-center gap-1" title="Jobs completed"><CheckCircle2 className="w-4 h-4 text-[#2E7D6B]" /> {h.completed}</span>
                  <span className="flex items-center gap-1" title="Average rating"><Star className="w-4 h-4 text-[#D99B26]" fill="#D99B26" /> {h.avg_rating || "–"}</span>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
